import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useCountUp } from 'react-countup'
import { useIntl, defineMessages } from 'react-intl'
import { Box, Card, Flex, Icon, Text, CardHeader, Heading } from '@homebotapp/hb-react-component-catalog'
import Format from '../../Format'
import BotHeadColored from '../../Avatar/BotHeadColored'
import { selectHomeUuid } from '../../../../store/selectors/home'
import { selectClientId } from '../../../../store/selectors/client'
import { selectSettingsMap } from '../../../../store/selectors/airDna'
import { useGetExtraPrincipalPaymentSavingsWithExtraPaymentQuery } from '../../../../store/slices/extraPrincipalPaymentSavings'
import { selectFirstCEL } from '../../../../store/selectors/home/loans'
import { formatCurrency } from '../../../../helpers/formatters'

export const MSG = defineMessages({
  highlight: {
    id: 'AirDNA.extraPayments.yearsRemoved.highlight',
    defaultMessage: '{yearsSavedFormatted} {yearsSaved, plural, one {year} other {years}} off'
  },
  principal: {
    id: 'AirDna.extraPayments.towardPrincipal',
    defaultMessage: "If you paid that money towards your principal, you'd save:"
  },
  savings: {
    id: 'AirDna.extraPayments.interestSavings',
    defaultMessage: '{savingsText} in interest'
  },
  yearsRemoved: {
    id: 'AirDNA.extraPayments.yearsRemoved',
    defaultMessage: '{yearsHighlightText} your loan'
  }
})

export interface AirDnaExtraPaymentsProps {
  stateKey: string
}

export const AirDnaExtraPayments = ({ stateKey }: AirDnaExtraPaymentsProps) => {
  const intl = useIntl()
  const loan = useSelector(selectFirstCEL)
  const homeUuid = useSelector(selectHomeUuid)
  const clientId = useSelector(selectClientId) as string
  const settings = useSelector(selectSettingsMap)[stateKey]
  const extraPayment = settings?.nightsPerMonth * settings?.pricePerNight

  const {
    data: extraPrincipalPaymentSavings = [],
    isFetching,
    isSuccess,
    refetch
  } = useGetExtraPrincipalPaymentSavingsWithExtraPaymentQuery(
    {
      clientId,
      homeUuid,
      extraPayment
    },
    {
      skip: !clientId || !homeUuid || !extraPayment
    }
  )

  const { countUp, update } = useCountUp({
    end: 0,
    start: 0,
    duration: 1
  })

  useEffect(() => {
    !isFetching && isSuccess && refetch() // If we have already fetched and the extraPayment is updated via the AirDnaChartSliders or the loan is updated - we need to refetch
  }, [
    extraPayment,
    loan?.date,
    loan?.rate,
    loan?.amount,
    loan?.balance,
    loan?.termYears,
    loan?.adjustable,
    loan?.armYearsInitial
  ])

  useEffect(() => {
    if (extraPrincipalPaymentSavings?.length) {
      update(extraPrincipalPaymentSavings[0].totalInterestSaved)
    }
  }, [extraPrincipalPaymentSavings])

  return (
    <Card textAlign='center' marginY={8}>
      <Box width='100%' marginTop={-4}>
        <BotHeadColored size='xs' />
      </Box>
      <CardHeader paddingY={0} paddingX={2}>
        <Heading as='h3' marginTop={4} marginBottom={0} size='sm' fontWeight='light'>
          {intl.formatMessage(MSG.principal)}
        </Heading>
      </CardHeader>
      <Flex gap={4} padding={6} align='center' justify='space-between' direction='column'>
        <Flex align='center'>
          <Icon marginEnd='2' name='check-circle' color='success.700' />
          <Text as='span'>
            {intl.formatMessage(MSG.savings, {
              savingsText: (
                <Text as='span' color='success'>
                  {/* round to the nearest $500 */}
                  {formatCurrency(Math.round((countUp as number) / 500) * 500)}
                </Text>
              )
            })}
          </Text>
        </Flex>
        <Flex align='center'>
          <Icon marginEnd='2' name='check-circle' color='success.700' />
          <Text as='span'>
            {intl.formatMessage(MSG.yearsRemoved, {
              yearsHighlightText: (
                <Text as='span' color='success'>
                  {intl.formatMessage(MSG.highlight, {
                    yearsSavedFormatted: (
                      <Format.Number value={Math.round(extraPrincipalPaymentSavings[0]?.timeSavedInMonths / 12)} />
                    ),
                    yearsSaved: Math.round(extraPrincipalPaymentSavings[0]?.timeSavedInMonths / 12)
                  })}
                </Text>
              )
            })}
          </Text>
        </Flex>
      </Flex>
    </Card>
  )
}
